import type { ErrorEvent } from '@sentry/core';

// PII patterns observed in production logs: email addresses and contiguous
// E.164 phone numbers. Names/addresses are too varied to match in free-form
// text — they are redacted only at the key level (see PII_FIELD_NAMES).
const EMAIL_RE = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;

// Only contiguous, plus-prefixed runs of 11-15 digits are treated as phone
// numbers in free text. This matches real international numbers with a country
// code (e.g. +573001234567) while leaving epoch timestamps, byte offsets,
// numeric ids and short/separator-grouped runs untouched.
const PHONE_RE = /\+\d{11,15}\b/g;

// Field names that are always PII regardless of value. Covers camelCase and
// snake_case variants. `\bname\b` restricts the bare-name match to a standalone
// token so we don't redact `eventName`, `hostname`, `fileName`, etc.
const PII_FIELD_NAMES =
  /email|phone|telephone|address|(?:full|first|last|display|user)_?name|\bname\b/i;

function redactString(value: string): string {
  return value.replace(EMAIL_RE, '[REDACTED:email]').replace(PHONE_RE, '[REDACTED:phone]');
}

function redactValue(key: string, value: unknown): unknown {
  if (value == null) return value;

  if (typeof value === 'string') {
    return PII_FIELD_NAMES.test(key) ? '[REDACTED]' : redactString(value);
  }

  if (Array.isArray(value)) {
    return value.map((v, i) => redactValue(typeof v === 'object' ? key : `${key}[${i}]`, v));
  }

  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      out[k] = redactValue(k, v);
    }
    return out;
  }

  return value;
}

/**
 * Sentry `beforeSend` PII redactor shared by every client (backend, admin,
 * mobile). Operates on a deep copy — never mutates the input event. On clone
 * failure the original event is returned unchanged.
 *
 * Redaction coverage (canonical superset):
 * - Drops auth headers (authorization, x-api-key, cookie).
 * - Scrubs event.user (email, username, ip_address).
 * - Redacts email + contiguous E.164 phone patterns in free-form strings.
 * - Replaces PII-keyed fields with `[REDACTED]` in request.data, extra,
 *   contexts, breadcrumb data and nested objects — regardless of value.
 * - Redacts breadcrumb + exception messages/values.
 *
 * Required by the H6 compliance gate: no PII in the error tracker or
 * breadcrumb stream.
 *
 * NOTE: Vendored copy of @flowsoft/observability. admin-panel builds as an
 * independent Docker image (isolated context) that cannot resolve the shared
 * workspace package, so the redactor lives here. Keep in sync with
 * packages/observability/src/pii-redactor.ts if that changes.
 */
export function redactPiiFromSentryEvent(event: ErrorEvent): ErrorEvent {
  let cloned: ErrorEvent;
  try {
    cloned = JSON.parse(JSON.stringify(event)) as ErrorEvent;
  } catch {
    return event;
  }

  if (cloned.request?.headers) {
    delete cloned.request.headers.authorization;
    delete cloned.request.headers['x-api-key'];
    delete cloned.request.headers.cookie;
  }

  if (cloned.user) {
    delete cloned.user.email;
    delete cloned.user.username;
    delete cloned.user.ip_address;
  }

  if (typeof cloned.message === 'string') {
    cloned.message = redactString(cloned.message);
  }

  if (cloned.request && cloned.request.data != null) {
    cloned.request.data = redactValue('data', cloned.request.data);
  }

  if (cloned.extra) {
    cloned.extra = redactValue('extra', cloned.extra) as Record<string, unknown>;
  }

  if (cloned.contexts) {
    cloned.contexts = redactValue('contexts', cloned.contexts) as Record<
      string,
      Record<string, unknown>
    >;
  }

  if (Array.isArray(cloned.breadcrumbs)) {
    cloned.breadcrumbs = cloned.breadcrumbs.map((b) => ({
      ...b,
      message: typeof b.message === 'string' ? redactString(b.message) : b.message,
      data: b.data ? (redactValue('data', b.data) as Record<string, unknown>) : b.data,
    }));
  }

  if (cloned.exception?.values) {
    cloned.exception.values = cloned.exception.values.map((v) => ({
      ...v,
      value: typeof v.value === 'string' ? redactString(v.value) : v.value,
    }));
  }

  return cloned;
}
